/**
 * shared/palette.ts — the colour catalogue behind RUNTIME recolouring.
 *
 * The hero ships as ONE drawn set (`canon`) plus an exact colour→colour table.
 * The pipeline already works this way: `tools/heropipeline/gen.py` draws canon
 * and derives every other tint as an exact remap of it ("tints share geometry
 * by construction — no re-render"), and `Palette.remap()` in
 * `tools/heropipeline/palette.py` refuses a canon colour shared by two classes
 * because it would make that swap ambiguous. That invariant is what makes
 * colour a runtime decision: **the canon palette is itself the region mask**, so
 * no mask art and no per-colour sprite tree is needed.
 *
 * So this module holds:
 *   - `canon` — the authored per-class 4-tone ramps (from the pipeline's
 *     `spec/palette.json`, which stays the art's source of truth);
 *   - `groups` — the RECOLOURABLE REGIONS (top / legs / hair / skin), each a
 *     set of canon material classes, from `palette.store`;
 *   - `swatches` — what a player may pick for a region: four explicit hexes
 *     (base + the three derived tones), authored in the knobs app's Look tab.
 *
 * A swatch stores four hexes rather than one so **the renderer does no colour
 * math at all** — it builds a hex→hex lookup and applies it. Colour math lives
 * in the editor (`deriveRamp`), where it is an authoring aid whose output is
 * written into the store as data.
 *
 * What is deliberately NOT recolourable: `metal|gold|wood|gem` (a hero's gear
 * metal is not a team colour), the outline, and the classes no region claims.
 * NPC, item and gear art stays baked — those are per-creature composites and
 * nobody customises them.
 */
import { PALETTE_DATA } from "./palette.data.js";

/** The four tone fields of a ramp, in shading order. */
export const TONE_FIELDS = ["base", "shadow", "light", "deep"] as const;
export type ToneField = (typeof TONE_FIELDS)[number];

/** A swatch: one region colour, stored as its full ramp. */
export interface Swatch {
  /** Permanent wire id within its region (sparse, never reused). `0` = canon. */
  id: number;
  name: string;
  base: string;
  shadow: string;
  light: string;
  deep: string;
}

/** A recolourable region: the canon material classes it paints, as one colour. */
export interface RegionGroup {
  /** Permanent wire key (`top`, `legs`, …) — what a look references. */
  key: string;
  /** Display label (editor, character creator). */
  label: string;
  /** Canon material classes this region paints together. */
  classes: readonly string[];
  swatches: readonly Swatch[];
}

/** The pipeline's tone rule: each tone is the base's hue with s/v scaled. */
export interface ToneRule {
  shadow: { v: number; s: number };
  light: { v: number; s: number };
  deep: { v: number; s: number };
}

export interface PaletteCatalog {
  canon: {
    outline: string;
    /** class -> [base, shadow, light, deep] */
    classes: Record<string, readonly string[]>;
  };
  toneRule: ToneRule;
  groups: readonly RegionGroup[];
}

/** The reserved swatch id meaning "no recolour — wear the canon ramp". */
export const CANON_SWATCH = 0;

/** A chosen look's colour half: region key -> swatch id (absent = canon). */
export type RegionColours = Record<string, number>;

export const DEFAULT_REGION_COLOURS: RegionColours = {};

const isHex = (v: unknown): v is string => typeof v === "string" && /^#[0-9a-f]{6}$/.test(v);

const isSwatch = (s: unknown): s is Swatch => {
  const o = s as Swatch;
  return (
    !!o &&
    Number.isInteger(o.id) &&
    o.id > CANON_SWATCH &&
    typeof o.name === "string" &&
    o.name.length > 0 &&
    TONE_FIELDS.every((t) => isHex(o[t]))
  );
};

const isGroup = (g: unknown): g is RegionGroup => {
  const o = g as RegionGroup;
  return (
    !!o &&
    typeof o.key === "string" &&
    /^[a-z][a-z0-9_]*$/.test(o.key) &&
    typeof o.label === "string" &&
    o.label.length > 0 &&
    Array.isArray(o.classes) &&
    o.classes.length > 0 &&
    o.classes.every((c) => typeof c === "string" && c.length > 0) &&
    Array.isArray(o.swatches) &&
    o.swatches.every(isSwatch)
  );
};

/**
 * Load + validate the projection. Everything here is a BUILD error, never a
 * runtime surprise: a look must always resolve to something renderable.
 *
 * The important check is the AMBIGUITY rule (same rule tools/palette-store.js
 * enforces on save): a recolour is one pass of exact colour -> colour, so a
 * source colour may resolve to only one target. Canon colours are NOT all
 * unique — `ink` reuses the outline colour, `glint` is one flat colour,
 * `shade_metal` repeats `metal` — and that is fine while nothing remappable
 * shares them. What would silently recolour the wrong pixels is a colour
 * shared between a recolourable region and anything else.
 */
export const PALETTE: PaletteCatalog = (() => {
  const cat = PALETTE_DATA as unknown as PaletteCatalog;
  if (!cat || typeof cat.canon !== "object" || typeof cat.canon.classes !== "object") {
    throw new Error("invalid palette catalog (palette.data.ts)");
  }
  if (!isHex(cat.canon.outline)) throw new Error("palette.data: canon outline is not a hex colour");

  for (const [cls, ramp] of Object.entries(cat.canon.classes)) {
    if (!Array.isArray(ramp) || ramp.length !== TONE_FIELDS.length || !ramp.every(isHex)) {
      throw new Error(`palette.data: canon class ${cls} needs ${TONE_FIELDS.length} hex colours`);
    }
  }

  // groups: known classes, claimed at most once, unique swatch ids.
  const regionOfClass = new Map<string, string>();
  const keys = new Set<string>();
  for (const g of cat.groups) {
    if (!isGroup(g)) throw new Error("palette.data: invalid region group");
    if (keys.has(g.key)) throw new Error(`palette.data: duplicate region ${g.key}`);
    keys.add(g.key);
    for (const c of g.classes) {
      if (!(c in cat.canon.classes)) throw new Error(`palette.data: region ${g.key} claims unknown canon class ${c}`);
      const prev = regionOfClass.get(c);
      if (prev !== undefined) throw new Error(`palette.data: class ${c} is claimed by both ${prev} and ${g.key} (a pixel could not pick a side)`);
      regionOfClass.set(c, g.key);
    }
    const ids = new Set<number>();
    for (const s of g.swatches) {
      if (ids.has(s.id)) throw new Error(`palette.data: region ${g.key} has swatch id ${s.id} twice`);
      ids.add(s.id);
    }
  }

  const owners = new Map<string, { cls: string; tone: ToneField }[]>();
  const addOwner = (hex: string, cls: string, tone: ToneField) => {
    const list = owners.get(hex) ?? [];
    list.push({ cls, tone });
    owners.set(hex, list);
  };
  for (const [cls, ramp] of Object.entries(cat.canon.classes)) {
    ramp.forEach((hex, i) => addOwner(hex, cls, TONE_FIELDS[i]!));
  }
  addOwner(cat.canon.outline, "outline", "base");
  for (const [hex, list] of owners) {
    const tinted = list.filter((o) => regionOfClass.has(o.cls));
    if (tinted.length === 0) continue;
    const who = tinted.map((o) => `${o.cls}.${o.tone}`).join(", ");
    const regions = new Set(tinted.map((o) => regionOfClass.get(o.cls)));
    if (regions.size > 1) {
      throw new Error(`palette.data: canon colour ${hex} is claimed by regions ${[...regions].join(", ")} (${who}) — one exact swap cannot serve both`);
    }
    if (list.length > tinted.length) {
      const others = list.filter((o) => !regionOfClass.has(o.cls)).map((o) => `${o.cls}.${o.tone}`).join(", ");
      throw new Error(`palette.data: canon colour ${hex} is shared by recolourable ${who} and ${others} — recolouring it would repaint ${others} too`);
    }
    // Same-tone owners (tunic.base and jacket.base) are fine: both take the
    // region's swatch.base, so one colour still resolves to one target.
    if (new Set(tinted.map((o) => o.tone)).size > 1) {
      throw new Error(`palette.data: canon colour ${hex} is ${who} — one colour at two tones has no single replacement in a region ramp`);
    }
  }
  return cat;
})();

export const regionGroups = (): readonly RegionGroup[] => PALETTE.groups;

export const groupByKey = (key: string): RegionGroup | null =>
  PALETTE.groups.find((g) => g.key === key) ?? null;

/** The swatch for a region choice, or null when it doesn't exist (a deleted
 *  swatch — callers fall back to canon rather than failing to render). */
export const swatchOf = (groupKey: string, id: number): Swatch | null => {
  if (id === CANON_SWATCH) return null;
  return groupByKey(groupKey)?.swatches.find((s) => s.id === id) ?? null;
};

/** The four tones a class paints under a chosen look (identity when uncoloured).
 *  Used by previews and tests; the renderer applies `colourLut` instead. */
export const rampForClass = (cls: string, colours: RegionColours): readonly string[] => {
  const canon = PALETTE.canon.classes[cls];
  if (!canon) throw new Error(`unknown canon class ${cls}`);
  const region = PALETTE.groups.find((g) => g.classes.includes(cls));
  const swatch = region ? swatchOf(region.key, colours[region.key] ?? CANON_SWATCH) : null;
  return swatch ? TONE_FIELDS.map((t) => swatch[t]) : canon;
};

/**
 * The exact colour→colour table for a look: every canon tone the look touches →
 * its replacement. Absent regions and canon swatches contribute nothing (an
 * absent key in the table means "leave the pixel alone"), so the table is
 * always a subset and the renderer can apply it blindly.
 *
 * Only the SOURCE side needs uniqueness, which the catalogue load already
 * checked. Two regions may pick the same colour — legal, just visually merged,
 * which is the editor's warning rather than a runtime error.
 *
 * The catalogue is injectable so the editor can preview UN-saved drafts
 * (its swatches live in the DOM, not in `PALETTE` yet); the renderer and
 * tests use the committed one by default.
 */
export const colourLut = (colours: RegionColours, cat: PaletteCatalog = PALETTE): Map<string, string> => {
  const lut = new Map<string, string>();
  for (const group of cat.groups) {
    const id = colours[group.key] ?? CANON_SWATCH;
    const swatch = id === CANON_SWATCH ? null : group.swatches.find((s) => s.id === id) ?? null;
    if (!swatch) continue;
    group.classes.forEach((cls) => {
      const canon = cat.canon.classes[cls];
      if (!canon) return;
      TONE_FIELDS.forEach((tone, i) => {
        const from = canon[i];
        if (from !== undefined) lut.set(from, swatch[tone]);
      });
    });
  }
  return lut;
};

/** True when every key is a known region and every id is a real swatch (canon
 *  allowed). Creation-time check: a client cannot store a look the palette
 *  can't render. Reading an OLD look is more forgiving — see `swatchOf`. */
export const isValidRegionColours = (v: unknown): v is RegionColours => {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  for (const [key, id] of Object.entries(v)) {
    const group = groupByKey(key);
    if (!group) return false;
    if (!Number.isInteger(id)) return false;
    if (id !== CANON_SWATCH && !group.swatches.some((s) => s.id === id)) return false;
  }
  return true;
};

// ---- authoring helper ----------------------------------------------------
//
// HSV derivation of the three derived tones from a base, mirroring the
// pipeline's `_tones_from_base` (same hue, s/v scaled by the tone rule, clamped
// to [0,1]). This is an EDITORIAL helper — the store keeps the resulting four
// hexes explicitly, so nothing at runtime derives anything. Byte-identical
// asset reproduction stays the pipeline's job; this only has to agree with the
// store it wrote (pinned by a test).

const hexToRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const rgbToHex = (r: number, g: number, b: number): string => {
  const part = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
};

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

const rgbToHsv = (r: number, g: number, b: number): [number, number, number] => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h /= 6;
    if (h < 0) h += 1;
  }
  const s = max === 0 ? 0 : d / max;
  return [h, s, max];
};

const hsvToRgb = (h: number, s: number, v: number): [number, number, number] => {
  const sn = clamp01(s);
  const vn = clamp01(v);
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = vn * (1 - sn);
  const q = vn * (1 - f * sn);
  const t = vn * (1 - (1 - f) * sn);
  switch (((i % 6) + 6) % 6) {
    case 0:
      return [vn, t, p];
    case 1:
      return [q, vn, p];
    case 2:
      return [p, vn, t];
    case 3:
      return [p, q, vn];
    case 4:
      return [t, p, vn];
    default:
      return [vn, p, q];
  }
};

/** The three derived tones for a base colour, through the catalogue's rule. */
export const deriveRamp = (
  base: string,
  rule: ToneRule = PALETTE.toneRule,
): { base: string; shadow: string; light: string; deep: string } => {
  if (!isHex(base)) throw new Error(`deriveRamp: bad base colour ${String(base)}`);
  const [h, s, v] = rgbToHsv(...hexToRgb(base));
  const tone = (scale: { v: number; s: number }) =>
    rgbToHex(...hsvToRgb(h, s * scale.s, v * scale.v).map((x) => Math.round(x * 255)) as [number, number, number]);
  return {
    base,
    shadow: tone(rule.shadow),
    light: tone(rule.light),
    deep: tone(rule.deep),
  };
};
